import * as React from 'react'
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Link,
    makeStyles,
    tokens,
} from '@fluentui/react-components'
import { ArrowSyncRegular } from '@fluentui/react-icons'
import { getId } from '@fluentui/utilities'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import DiagnosticTextField from './DiagnosticTextField'
import LabelValue from './LabelValue'
import { Build } from '../../../components/Diagnostics/Version'
import useUser from '../../../hooks/useUser'
import useModules from '../../../hooks/module/useModules'
import useSharePoint, { formatDriveId } from '../../../hooks/useSharePoint'
import { parseDateTime } from '../../../helpers/dateHelper'
import AuthorizationContext, { SecurityAuthority } from '../../../context/AuthorizationContext'
import { loginJourneyId } from '../../../helpers/loginTelemetry'

type Props = {
    open: boolean
    onOpenChange: (open: boolean) => void
}

type URLSearchParamsExt = URLSearchParams & Map<string, string>

const useStyles = makeStyles({
    surface: {
        maxWidth: '720px',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalM,
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalS,
    },
})

const SharePointDriveId: FunctionComponent = () => {
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const { currentEmail } = useUser()
    const { sharePoint, setDriveId, errorMessage } = useSharePoint()

    const [searchParams, setSearchParams] = useState<URLSearchParams>(new URLSearchParams())

    useEffect(() => {
        setSearchParams(
            new URLSearchParams({
                userEmail: currentEmail,
                webTitle: 'HAMMOCK',
                listTitle: 'Documenten',
                webUrl: sharePoint?.site?.webUrl,
                siteId: sharePoint?.siteId,
                webId: sharePoint?.webId,
                listId: sharePoint?.listId,
            }),
        )
    }, [currentEmail, sharePoint?.site])

    return (
        <div className={styles.section}>
            <DiagnosticTextField
                label={'Drive ID'}
                value={sharePoint?.driveId}
                onChange={value => setDriveId(value?.replace('_', '/').replace('-', '+'))}
                errorMessage={errorMessage}
            />
            {sharePoint && sharePoint.site && sharePoint.list && (
                <Button icon={<ArrowSyncRegular />} onClick={() => window.open(`odopen://sync?${searchParams.toString()}`, '_blank')}>
                    {translate('support.toolbox.diagnostics.sync')}
                </Button>
            )}
            {sharePoint && [
                <LabelValue key={getId()} label={'Drive: siteId'} value={sharePoint.siteId} />,
                <LabelValue key={getId()} label={'Drive: webId'} value={sharePoint.webId} />,
                <LabelValue key={getId()} label={'Drive: listId'} value={sharePoint.listId} />,
            ]}
            {sharePoint?.site && [
                <LabelValue key={getId()} label={'Site: displayName'} value={sharePoint.site.displayName} />,
                <LabelValue
                    key={getId()}
                    label={'Site: webUrl'}
                    value={
                        <Link href={sharePoint.site.webUrl} target={'_blank'}>
                            {sharePoint.site.webUrl}
                        </Link>
                    }
                />,
            ]}
            {sharePoint?.list && [
                <LabelValue key={getId()} label={'List: displayName'} value={sharePoint.list.displayName} />,
                <LabelValue
                    key={getId()}
                    label={'List: webUrl'}
                    value={
                        <Link href={sharePoint.list.webUrl} target={'_blank'}>
                            {sharePoint.list.webUrl}
                        </Link>
                    }
                />,
            ]}
        </div>
    )
}

const SharePointLibraryId: FunctionComponent = () => {
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const [libraryId, setLibraryId] = useState<string>()
    const [labelValues, setLabelValues] = useState<{ label: string; value: string }[]>([])

    useEffect(() => {
        const params = new URLSearchParams(libraryId) as URLSearchParamsExt
        const driveId = formatDriveId({
            driveId: undefined,
            siteId: params.get('siteId'),
            webId: params.get('webId'),
            listId: params.get('listId'),
        })

        setLabelValues(
            [
                driveId && { label: 'driveId', value: driveId },
                ...[...params.entries()].map(([label, value]) => ({
                    label,
                    value: value.replaceAll(/(^{)|(}$)/g, ''),
                })),
            ].filter(item => item),
        )
    }, [libraryId])

    return (
        <div className={styles.section}>
            <DiagnosticTextField label={translate('support.toolbox.diagnostics.libraryUrl')} value={libraryId} onChange={setLibraryId} />
            {labelValues.map(labelValue => (
                <LabelValue key={getId()} label={labelValue.label} value={labelValue.value} />
            ))}
        </div>
    )
}

const SharePointPanel: FunctionComponent = () => {
    const styles = useStyles()
    const { tokens: authTokens } = useContext(AuthorizationContext)

    return (
        <div className={styles.section}>
            <SharePointDriveId />
            <DiagnosticTextField label={'Token'} multiline readOnly rows={5} value={authTokens?.[SecurityAuthority.SharePoint]?.access_token} />
            <SharePointLibraryId />
        </div>
    )
}

const HammockPanel: FunctionComponent = () => {
    const styles = useStyles()
    const { tokens: authTokens, roles } = useContext(AuthorizationContext)
    const { t: translate } = useTranslation()
    const { modules } = useModules()

    return (
        <div className={styles.section}>
            <DiagnosticTextField label={'Token'} multiline readOnly rows={5} value={authTokens?.[SecurityAuthority.Hammock]?.access_token} />
            <LabelValue label={translate('support.toolbox.diagnostics.roles')} value={roles?.join(', ')} />
            <LabelValue
                label={translate('support.toolbox.diagnostics.modules')}
                value={modules?.map(module => translate(`file.module.${module.type?.toLowerCase()}`) ?? 'unknown').join(', ')}
            />
        </div>
    )
}

const WebPubSubPanel: FunctionComponent = () => {
    const styles = useStyles()
    const { tokens: authTokens } = useContext(AuthorizationContext)
    const { t: translate } = useTranslation()

    return (
        <div className={styles.section}>
            <DiagnosticTextField label={'Token'} multiline readOnly rows={5} value={authTokens?.[SecurityAuthority.WebPubSub]?.access_token} />
            <LabelValue
                label={translate('support.toolbox.diagnostics.expirydate')}
                value={parseDateTime(DateTime.fromISO(authTokens?.[SecurityAuthority.WebPubSub]?.expires_at)).toFormat('dd-LL-yyyy HH:mm:ss')}
            />
        </div>
    )
}

/**
 * v9 replacement for the legacy (Fluent-v8 Dialog/Fieldset) DiagnosticsDialog. Shows environment info and, when
 * authenticated, collapsible token panels for SharePoint/Hammock/Web PubSub.
 */
const DiagnosticsDialog: FunctionComponent<Props> = props => {
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const { authenticated, mailbox, user, host: currentHost } = useContext(AuthorizationContext)

    const lastCommitDateTime = DateTime.fromISO(process.env.LASTCOMMITDATETIME).toFormat('dd-LL-yyyy HH:mm:ss')
    const gitString = `Git ${process.env.COMMITHASH.substring(0, 8)} @ ${lastCommitDateTime}`

    const environment = `Env: ${currentHost?.Name || 'Geen omgeving'}
Version: v${(process.env.BUILD as unknown as Build).version}
Office?: ${Office?.context?.platform} - ${Office?.context?.diagnostics?.version}
Mailbox: ${mailbox || 'Geen mailbox'}
User: ${user?.username || 'Geen gebruiker'}
Session: ${loginJourneyId}
Git: ${gitString}
Url: ${window.location.href}
Agent: ${window.navigator.userAgent}`

    return (
        <Dialog open={props.open} onOpenChange={(_event, data) => props.onOpenChange(data.open)}>
            <DialogSurface className={styles.surface}>
                <DialogBody>
                    <DialogTitle>{translate('support.toolbox.diagnostics.title')}</DialogTitle>
                    <DialogContent className={styles.content}>
                        <DiagnosticTextField multiline readOnly rows={9} value={environment} />
                        {authenticated && (
                            <Accordion collapsible multiple>
                                <AccordionItem value={'sharepoint'}>
                                    <AccordionHeader>SharePoint</AccordionHeader>
                                    <AccordionPanel>
                                        <SharePointPanel />
                                    </AccordionPanel>
                                </AccordionItem>
                                <AccordionItem value={'hammock'}>
                                    <AccordionHeader>Hammock</AccordionHeader>
                                    <AccordionPanel>
                                        <HammockPanel />
                                    </AccordionPanel>
                                </AccordionItem>
                                <AccordionItem value={'webpubsub'}>
                                    <AccordionHeader>Web PubSub</AccordionHeader>
                                    <AccordionPanel>
                                        <WebPubSubPanel />
                                    </AccordionPanel>
                                </AccordionItem>
                            </Accordion>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button appearance={'primary'} onClick={() => props.onOpenChange(false)}>
                            {translate('action.close')}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    )
}

export default DiagnosticsDialog
